import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../../lib/api";
import { RefreshCw, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";

const DAY_PRESETS = [
  { id: 1, label: "Today" },
  { id: 7, label: "Last 7 days" },
  { id: 14, label: "Last 14 days" },
  { id: 30, label: "Last 30 days" },
  { id: 60, label: "Last 60 days" },
  { id: 90, label: "Last 90 days" },
];

export default function CurrentRoasBlock({ block, isTeam, onUpdateBlock }) {
  const { id: clientId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const cfg = block.config || {};
  const days = cfg.days || 30;
  const goal = parseFloat(cfg.goal) || 0;

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await api.get(`/clients/${clientId}/roas?days=${days}`);
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load ROAS data");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, days]);

  useEffect(() => {
    const onCk = (e) => { if (e.detail?.client_id === clientId) load(); };
    window.addEventListener("tt-checkin-updated", onCk);
    return () => window.removeEventListener("tt-checkin-updated", onCk);
    // eslint-disable-next-line
  }, [clientId, days]);

  // Sparkline points for last 30 buckets (only those with samples)
  const points = useMemo(() => (data?.series || []).filter((s) => s.samples > 0).slice(-30), [data]);
  const maxRoas = Math.max(0.1, ...(points.map((p) => p.roas) || [0]));
  const current = data?.current;
  const delta = (current != null && data?.avg != null) ? (current - data.avg) : null;

  return (
    <div data-testid="roas-block">
      <div className="flex items-center justify-between mb-3">
        <span className="t-eyebrow">Current ROAS · {cfg.title || (days === 1 ? "Today" : `Last ${days} days`)}</span>
        <div className="flex items-center gap-2">
          {isTeam && (
            <select value={days}
              onChange={(e) => onUpdateBlock(block.id, { config: { ...cfg, days: parseInt(e.target.value) } })}
              className="bg-[#0a0a0a] border border-zinc-800 rounded-md h-8 text-[11px] px-2"
              data-testid="roas-days">
              {DAY_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          )}
          <Button size="sm" variant="ghost" onClick={load} className="h-8 w-8 p-0" title="Refresh" data-testid="roas-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {err ? (
        <div className="flex items-start gap-2 text-[12px] text-[#FCA5A5] flag-red rounded-md p-3">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <div>
            <div className="font-medium">{err}</div>
          </div>
        </div>
      ) : !data ? (
        <div className="text-zinc-600 text-[12px]">Loading…</div>
      ) : (data.fields || []).length === 0 ? (
        <div className="text-zinc-600 text-[12px]">No ROAS check-in field configured. Add one in Client Settings → Check-in fields with type <em>ROAS today</em>.</div>
      ) : current == null ? (
        <div className="text-zinc-600 text-[12px]">No ROAS check-ins logged in this range yet.</div>
      ) : (
        <div className="space-y-4">
          {/* Big current ROAS card */}
          <div className="rounded-lg bg-white text-black p-5 sm:p-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div className="min-w-0">
              <div className="t-eyebrow text-[9px] text-black/60">Current ROAS · {data.current_date}</div>
              <div className="font-display text-[44px] sm:text-[56px] font-semibold leading-none mt-1" data-testid="roas-current">
                {current.toFixed(2)}<span className="text-[22px] sm:text-[28px] text-black/40 ml-1">x</span>
              </div>
              {goal > 0 && (
                <div className="text-[12px] mt-1 font-mono">
                  Goal {goal.toFixed(2)}x ·
                  <span className={current >= goal ? " text-emerald-700 font-semibold" : " text-[#DC2626] font-semibold"}>
                    {" "}{current >= goal ? "ON TARGET" : "BELOW TARGET"}
                  </span>
                </div>
              )}
            </div>
            <div className="flex sm:flex-col sm:items-end gap-3 sm:gap-1.5 shrink-0">
              <div className="text-right">
                <div className="t-eyebrow text-[9px] text-black/50">{days === 1 ? "vs prior" : "vs range avg"}</div>
                <div className={`font-display text-[16px] font-semibold mt-0.5 inline-flex items-center gap-1 ${delta > 0 ? "text-emerald-700" : delta < 0 ? "text-[#DC2626]" : "text-black/70"}`}>
                  {delta > 0 ? <TrendingUp className="w-4 h-4" /> : delta < 0 ? <TrendingDown className="w-4 h-4" /> : null}
                  {delta != null ? `${delta >= 0 ? "+" : ""}${delta.toFixed(2)}x` : "—"}
                </div>
                <div className="text-[10px] font-mono text-black/40">avg {data.avg?.toFixed(2)}x</div>
              </div>
              <div className="text-right">
                <div className="t-eyebrow text-[9px] text-black/50">Best</div>
                <div className="font-display text-[15px] font-semibold mt-0.5">{data.best ? `${data.best.roas.toFixed(2)}x` : "—"}</div>
                <div className="text-[10px] font-mono text-black/40">{data.best?.date}</div>
              </div>
            </div>
          </div>

          {/* Sparkline */}
          {points.length > 1 && (
            <div className="tt-card p-3" data-testid="roas-spark">
              <div className="flex items-center justify-between mb-2">
                <div className="t-eyebrow text-[9px]">Trend · last {points.length} entries</div>
                {goal > 0 && <div className="text-[10px] font-mono text-zinc-500">— goal {goal.toFixed(2)}x</div>}
              </div>
              <svg viewBox={`0 0 ${points.length * 12} 60`} preserveAspectRatio="none" className="w-full h-16">
                {goal > 0 && (() => {
                  const y = 60 - Math.min(60, (goal / maxRoas) * 60);
                  return <line x1={0} x2={points.length * 12} y1={y} y2={y} stroke="rgba(255,255,255,0.18)" strokeDasharray="2 3" />;
                })()}
                {points.length > 1 && (() => {
                  const path = points.map((p, i) => {
                    const x = i * 12;
                    const y = 60 - Math.min(60, (p.roas / maxRoas) * 60);
                    return `${i === 0 ? "M" : "L"}${x},${y}`;
                  }).join(" ");
                  return <path d={path} fill="none" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />;
                })()}
                {points.map((p, i) => {
                  const x = i * 12;
                  const y = 60 - Math.min(60, (p.roas / maxRoas) * 60);
                  return <circle key={p.date} cx={x} cy={y} r={2.4} fill={goal > 0 && p.roas < goal ? "#EF4444" : "#fff"}>
                    <title>{`${p.date} · ${p.roas.toFixed(2)}x`}</title>
                  </circle>;
                })}
              </svg>
              <div className="flex justify-between text-[9px] text-zinc-700 font-mono mt-1">
                <span>{points[0]?.date.slice(5)}</span>
                <span>{points[points.length - 1]?.date.slice(5)}</span>
              </div>
            </div>
          )}

          {isTeam && (
            <div className="flex items-center gap-2 text-[11px] text-zinc-500 font-mono">
              <span>Goal:</span>
              <input type="number" step="0.01" value={cfg.goal || ""}
                onChange={(e) => onUpdateBlock(block.id, { config: { ...cfg, goal: e.target.value } })}
                placeholder="e.g. 2.5"
                className="bg-[#0a0a0a] border border-zinc-800 rounded-md h-7 px-2 text-[11px] w-20 font-mono"
                data-testid="roas-goal" />
              <span className="text-zinc-700">x · used to flag below-target days</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
